import { useMemo, useState } from 'react';
import { INSTRUMENTS } from '../lib/instruments';
import { useSelection } from '../lib/selection';
import { api, type Note, type NoteScope } from '../lib/api';
import { useAsync } from '../lib/dataHooks';
import { scopeLabel } from '../lib/scopeKey';

/**
 * 笔记浏览器（移动端「笔记」页 + 桌面全屏入口）
 *
 * 月分组 + 全文检索 + 标签筛选。
 * 检索走服务端 FTS5（`/api/notes?q=`），而非前端过滤 ——
 * 笔记会累积到数千篇，前端过滤既慢又会漏掉分页之外的内容。
 */
export function NoteBrowser() {
  const { instrument, select, setDate } = useSelection();
  const [scopeFilter, setScopeFilter] = useState<NoteScope | ''>('');
  const [q, setQ] = useState('');
  const [tag, setTag] = useState<string | null>(null);

  // 笔记已只有日 scope，列表按日期区间取（scopeFilter 只用于搜索时的标签过滤）
  const listQ = useAsync(
    () => api.notes(instrument.code, '2010-01-01', '2999-12-31'),
    [instrument.code],
    { keepPrevious: true },
  );
  /** 搜索：至少 2 个字符才发请求（FTS 前缀查询对单字噪声大） */
  const searchQ = useAsync(
    () => api.searchNotes(q, { code: instrument.code, tag: tag ?? undefined }),
    [q, instrument.code, tag],
    { enabled: q.trim().length >= 2 },
  );
  const tagListQ = useAsync(() => api.noteTags(), []);

  /**
   * 搜索需 ≥2 字符（FTS 前缀查询对单字噪声太大）。
   * 但「输入了字但没搜索」必须**显式提示** —— 否则静默回落成全部笔记列表，
   * 用户会以为搜索已生效且「搜不到」。
   */
  const trimmed = q.trim();
  const tooShort = trimmed.length > 0 && trimmed.length < 2;
  const searching = trimmed.length >= 2;
  const items = (searching ? searchQ.data?.items ?? [] : listQ.data?.items ?? [])
    .filter((n) => !tag || n.tags.includes(tag));

  const byMonth = useMemo(() => groupByMonth(items), [items]);
  const active = searching ? searchQ : listQ;

  return (
    <div className="flex flex-col">
      <div className="flex h-[52px] shrink-0 items-center gap-2 border-b px-3">
        <span className="text-[15px] font-semibold">笔记</span>
        <select
          value={instrument.code}
          onChange={(e) => { select(e.target.value); setQ(''); }}
          className="rounded border px-1.5 py-0.5 text-[12px]"
          style={{ background: 'var(--bg-surface)', color: 'var(--text-1)' }}
        >
          {INSTRUMENTS.map((i) => <option key={i.code} value={i.code}>{i.name}</option>)}
        </select>
        <span className="ml-auto num text-[11px]" style={{ color: 'var(--text-3)' }}>
          {active.status === 'loading' ? '…' : `${items.length} 篇`}
        </span>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b px-3 py-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="搜索笔记全文…"
          className="h-8 min-w-[120px] flex-1 rounded-[6px] border px-2 text-[12px] outline-none"
          style={{ background: 'var(--bg-inset)', color: 'var(--text-1)' }}
        />
        <select
          value={scopeFilter}
          onChange={(e) => setScopeFilter(e.target.value as NoteScope | '')}
          className="h-8 rounded-[6px] border px-2 text-[12px]"
          style={{ background: 'var(--bg-surface)', color: 'var(--text-2)' }}
        >
          <option value="">全部周期</option>
          <option value="day">日</option>
          <option value="week">周</option>
          <option value="month">月</option>
          <option value="year">年</option>
        </select>
      </div>

      {/* 标签筛选 */}
      {(tagListQ.data?.items.length ?? 0) > 0 && (
        <div className="flex shrink-0 flex-wrap gap-1.5 border-b px-3 py-1.5">
          <button type="button" onClick={() => setTag(null)}
            className="rounded-full border px-1.5 py-0.5 text-[11px]"
            style={{ borderColor: tag ? 'var(--border)' : 'var(--accent)',
              color: tag ? 'var(--text-3)' : 'var(--accent)' }}>全部</button>
          {tagListQ.data!.items.slice(0, 20).map((t) => (
            <button key={t.tag} type="button" onClick={() => setTag(tag === t.tag ? null : t.tag)}
              className="rounded-full border px-1.5 py-0.5 text-[11px]"
              style={{ borderColor: tag === t.tag ? 'var(--accent)' : 'var(--border)',
                color: tag === t.tag ? 'var(--accent)' : 'var(--text-2)' }}>
              {t.tag} {t.count}
            </button>
          ))}
        </div>
      )}

      {active.status === 'error' && (
        <div className="px-3 py-2 text-[12px]" style={{ color: 'var(--warn)' }}>⚠ {active.error}</div>
      )}

      <div>
        {byMonth.map(([month, list]) => (
          <section key={month}>
            <h2 className="sticky top-0 px-3 py-1.5 text-[11px]"
              style={{ background: 'var(--bg-base)', color: 'var(--text-3)' }}>
              {month} · {list.length}
            </h2>
            {list.map((n) => <NoteRow key={`${n.scope}|${n.scopeKey}`} note={n} onOpen={() => {
              if (n.scope === 'day') setDate(n.scopeKey);
            }} />)}
          </section>
        ))}

        {tooShort && (
          <div className="px-3 py-6 text-center text-[12px]" style={{ color: 'var(--text-3)' }}>
            请输入至少 2 个字符再搜索
          </div>
        )}

        {active.status === 'ready' && items.length === 0 && !tooShort && (
          <div className="px-3 py-6 text-center text-[12px]" style={{ color: 'var(--text-3)' }}>
            {searching ? `无匹配「${trimmed}」的笔记` : `${instrument.name} 尚无笔记`}
          </div>
        )}
      </div>
    </div>
  );
}

function NoteRow({ note, onOpen }: { note: Note; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="block w-full border-b px-3 py-2 text-left"
    >
      <div className="num flex items-center gap-2 text-[11px]" style={{ color: 'var(--text-3)' }}>
        <span>{scopeLabel(note.scope as never, note.scopeKey)}</span>
        {note.tags.slice(0, 3).map((t) => (
          <span key={t} className="rounded-full border px-1 text-[10px]"
            style={{ borderColor: 'var(--border)', color: 'var(--text-3)' }}>{t}</span>
        ))}
        <span className="ml-auto">{note.updatedAt.slice(5, 16)}</span>
      </div>
      <div className="line-clamp-2 mt-0.5 text-[13px] leading-[1.5]" style={{ color: 'var(--text-1)' }}>
        {note.content.replace(/[#*`>\-|]/g, '').trim() || '（空）'}
      </div>
    </button>
  );
}

/** 按 scopeKey 前 7 位（YYYY-MM）分组，降序 */
function groupByMonth(notes: Note[]): [string, Note[]][] {
  const map = new Map<string, Note[]>();
  for (const n of notes) {
    const key = n.scopeKey.length >= 7 ? `${n.scopeKey.slice(0, 7)}` : n.scopeKey;
    const arr = map.get(key) ?? [];
    arr.push(n);
    map.set(key, arr);
  }
  return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]));
}